import React, { useEffect, useMemo, useState } from 'react';
import type { ProDocumentFilters, DocumentTypeOption } from '../../../../types/proDocuments';
import MultiSelect, { type MultiSelectOption } from '../../../../components/MultiSelect';
import DateTimePicker, { type DateTimeValue } from '../../../../components/DateTimePicker';

interface DocsFilterBarProps {
  value: ProDocumentFilters;
  onChange: (next: ProDocumentFilters) => void;
  docTypes: DocumentTypeOption[];
}

// Mirrors DocsTab: deleted docs are shown by default (V1 parity).
const DEFAULT_FILTERS: ProDocumentFilters = { revision: 'all' };

const toDate = (s: string | undefined): Date | undefined =>
  s ? new Date(s + 'T00:00:00') : undefined;

const fromDate = (d: Date | undefined): string | undefined => {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DocsFilterBar: React.FC<DocsFilterBarProps> = ({ value, onChange, docTypes }) => {
  const [draft, setDraft] = useState<ProDocumentFilters>(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const dateFromValue = useMemo(() => toDate(draft.date_from) ?? null, [draft.date_from]);
  const dateToValue = useMemo(() => toDate(draft.date_to) ?? null, [draft.date_to]);

  const handleDateFromChange = (v: Date | DateTimeValue | null) => {
    const d = v instanceof Date ? v : v?.date;
    setDraft(prev => ({ ...prev, date_from: fromDate(d ?? undefined) }));
  };

  const handleDateToChange = (v: Date | DateTimeValue | null) => {
    const d = v instanceof Date ? v : v?.date;
    setDraft(prev => ({ ...prev, date_to: fromDate(d ?? undefined) }));
  };

  const docTypeOptions = useMemo<MultiSelectOption[]>(
    () =>
      docTypes.map(dt => ({
        value: dt.documenttype,
        label: dt.description ? `${dt.documenttype} — ${dt.description}` : dt.documenttype,
      })),
    [docTypes],
  );

  const handleDocTypeChange = (next: string[]) => {
    setDraft(prev => ({
      ...prev,
      documenttype: next.length > 0 ? next : undefined,
    }));
  };

  const REVISION_OPTIONS: MultiSelectOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'deleted', label: 'Deleted' },
    { value: 'all', label: 'All' },
  ];

  // Revision is conceptually single-select but rendered through MultiSelect
  // for visual consistency with Doc Type(s). Keep only the last-picked value.
  const handleRevisionChange = (next: string[]) => {
    const last = next[next.length - 1] as
      | 'active'
      | 'deleted'
      | 'all'
      | undefined;
    setDraft(prev => ({ ...prev, revision: last ?? 'all' }));
  };

  const handleFilterClick = () => {
    onChange({ ...draft });
  };

  const handleResetClick = () => {
    setDraft({ ...DEFAULT_FILTERS });
    onChange({ ...DEFAULT_FILTERS });
  };

  return (
    <div className="flex flex-wrap items-end gap-3 p-2 border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800">
      <div className="flex flex-col">
        <span
          aria-hidden="true"
          className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-600 dark:text-gray-300"
        >
          Date Range from
        </span>
        <DateTimePicker
          mode="date"
          selectionType="single"
          value={dateFromValue}
          onChange={handleDateFromChange}
          inputVariant="compact"
          labelType="none"
          placeholder="From"
          autoApply
        />
      </div>

      <div className="flex flex-col">
        <span
          aria-hidden="true"
          className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-600 dark:text-gray-300"
        >
          Date Range to
        </span>
        <DateTimePicker
          mode="date"
          selectionType="single"
          value={dateToValue}
          onChange={handleDateToChange}
          inputVariant="compact"
          labelType="none"
          placeholder="To"
          autoApply
        />
      </div>

      <div className="flex flex-col min-w-[220px]">
        <span
          aria-hidden="true"
          className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-600 dark:text-gray-300"
        >
          Doc Type(s)
        </span>
        <MultiSelect
          options={docTypeOptions}
          value={draft.documenttype ?? []}
          onChange={handleDocTypeChange}
          placeholder="All doc types"
          enableSearch
          size="sm"
          preserveOrder
        />
      </div>

      <div className="flex flex-col min-w-[140px]">
        <span
          aria-hidden="true"
          className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-600 dark:text-gray-300"
        >
          Revision
        </span>
        <MultiSelect
          options={REVISION_OPTIONS}
          value={[draft.revision ?? 'all']}
          onChange={handleRevisionChange}
          placeholder="All"
          size="sm"
          preserveOrder
        />
      </div>

      <button
        type="button"
        aria-label="Apply filters"
        onClick={handleFilterClick}
        className="px-3 py-1 bg-blue-600 dark:bg-blue-700 text-white text-sm font-semibold rounded hover:bg-blue-700 dark:hover:bg-blue-600"
      >
        Filter
      </button>

      <button
        type="button"
        aria-label="Reset filters"
        onClick={handleResetClick}
        className="px-3 py-1 bg-slate-200 dark:bg-gray-700 text-slate-800 dark:text-gray-100 text-sm font-semibold rounded hover:bg-slate-300 dark:hover:bg-gray-600"
      >
        Reset
      </button>
    </div>
  );
};

export default DocsFilterBar;
